import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import hallalIcon from '../assets/hallal.svg';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import '../styles/hero.css';

const HeroSlider: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const navigate = useNavigate();
  const { config } = useSiteConfig();

  // WhatsApp Link Component
  const WhatsAppLink: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => {
    // WhatsApp numbers based on language
    const whatsappNumbers = {
      en: config.phoneNumberTony, // Tony
      id: config.phoneNumberNila  // Nila
    };

    // Default messages based on language
    const defaultMessages = {
      en: config.whatsappMessageTonyEn,
      id: config.whatsappMessageNilaId
    };

    const phoneNumber = whatsappNumbers[currentLanguage as keyof typeof whatsappNumbers] || whatsappNumbers.en;
    const message = defaultMessages[currentLanguage as keyof typeof defaultMessages] || defaultMessages.en;
    const encodedMessage = encodeURIComponent(message);

    const handleClick = () => {
      // Track WhatsApp click
      if (window.gtag) {
        window.gtag('event', 'whatsapp_click', {
          event_category: 'Contact',
          event_label: `WhatsApp ${currentLanguage.toUpperCase()}`,
        });
      }
    };

    return (
      <a
        href={`https://wa.me/${phoneNumber.replace(/\D/g, '')}?text=${encodedMessage}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className={className}
      >
        {children}
      </a>
    );
  };

  // Get slides data from dynamic config
  const getSlides = () => {
    const isEn = i18n.language === 'en' || currentLanguage === 'en';
    return config.slides.map((slide, index) => ({
      image: slide.src,
      lazy: slide.lazy,
      badge: isEn ? slide.badge.en : slide.badge.id,
      title: isEn ? slide.title.en : slide.title.id,
      subtitle: isEn ? slide.subtitle.en : slide.subtitle.id,
      ctaLabel: slide.ctaLabel ? (isEn ? slide.ctaLabel.en : slide.ctaLabel.id) : undefined,
      ctaLink: slide.ctaLink,
      hidePrimaryCta: !!slide.hidePrimaryCta,
      categoryLink: 'all'
    }));
  };

  const slides = getSlides();

  useEffect(() => {
    if (isAutoPlay) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlay, slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Don't navigate if clicking on buttons, links, or their children
    if (!target.closest('a') && !target.closest('button')) {
      const slide = slides[currentSlide];
      const category = (slide as any).categoryLink || 'all';
      navigate(`/portfolio?category=${category}`);
    }
  };

  return (
    <div
      className="relative h-[95vh] md:h-[78vh] w-full overflow-hidden bg-gradient-to-br from-pink-50 via-white to-red-50 mb-8 md:mb-0 cursor-pointer md:cursor-default"
      onClick={handleContainerClick}
    >
      {/* Background with Image */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 1, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          {/* Mobile: Full background image with overlay */}
          <div className="h-full w-full md:flex md:flex-row">
            {/* Mobile: Full screen background image */}
            <div className="absolute inset-0 md:relative md:flex-1 md:order-2">
              <div
                className="h-full w-full bg-cover bg-center bg-no-repeat relative"
                style={{ backgroundImage: `url(${slides[currentSlide].image})` }}
              >
                {/* Mobile: Strong gradient overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/60 md:bg-gradient-to-r md:from-white/60 md:via-transparent md:to-transparent"></div>
              </div>
            </div>

            {/* Desktop: Left content area */}
            <div className="hidden md:flex md:flex-1 md:bg-gradient-to-br md:from-gray-50 md:via-white md:to-gray-100 md:relative md:order-1">
              {/* Decorative Background Elements - Desktop only */}
              <div className="absolute top-20 left-10 w-32 h-32 bg-green-100 rounded-full opacity-20"></div>
              <div className="absolute bottom-32 left-20 w-24 h-24 bg-green-200 rounded-full opacity-30"></div>
              <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-green-300 rounded-full opacity-20"></div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Content Overlay */}
      <div className="absolute inset-0 flex items-center justify-center md:items-center md:justify-start">
        <div className="container mx-auto px-6 md:px-8 lg:px-16 py-8 md:py-0">
          <div className="max-w-2xl mx-auto md:mx-0 text-center md:text-left">
            <motion.div
              key={`content-${currentSlide}`}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative z-10"
            >
              {/* Badge - Mobile optimized */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="inline-block mb-4 md:mb-6"
              >
                <span className="px-4 py-2 bg-gradient-to-r from-red-400 to-pink-500 text-white text-sm font-semibold rounded-full shadow-lg">
                  {slides[currentSlide].badge}
                </span>
              </motion.div>

              {/* Main Title - Better mobile typography */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6 text-white md:text-gray-800 leading-tight drop-shadow-lg md:drop-shadow-none"
                style={{ whiteSpace: 'pre-line' }}
              >
                {slides[currentSlide].title}
              </motion.h1>

              {/* Subtitle - Better mobile readability */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-lg md:text-xl mb-8 md:mb-8 text-white/90 md:text-gray-600 leading-relaxed max-w-lg drop-shadow md:drop-shadow-none"
              >
                {slides[currentSlide].subtitle}
              </motion.p>

              {/* CTA Button & Google Review Badge - Enhanced mobile design */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-4"
              >
                {!slides[currentSlide]?.hidePrimaryCta && (
                  <div className="hidden md:flex flex-col sm:flex-row gap-3 items-center">
                    <Link
                      to="/inquiry"
                      className="inline-flex items-center justify-center px-10 py-4 bg-white text-red-500 md:bg-gradient-to-r md:from-red-400 md:to-pink-500 md:text-white font-bold rounded-full hover:bg-gray-100 md:hover:from-red-500 md:hover:to-pink-600 transition-all duration-300 transform hover:scale-105 shadow-xl text-lg"
                    >
                      Shop Now
                    </Link>
                    <WhatsAppLink className="flex items-center justify-center gap-2 px-4 py-4 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors duration-300 shadow-lg hover:shadow-xl">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.888 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                      </svg>
                      <span className="text-sm font-medium">WhatsApp Us</span>
                    </WhatsAppLink>
                  </div>
                )}
                {(slides[currentSlide] as any)?.ctaLink && (
                  <Link
                    to={(slides[currentSlide] as any).ctaLink as string}
                    className="inline-flex items-center justify-center px-8 py-3 rounded-full text-lg font-semibold transition-all duration-300 shadow-md
                               border border-white/80 text-white bg-white/10 backdrop-blur-sm hover:bg-white hover:text-gray-900
                               md:border-gray-900 md:text-gray-900 md:bg-transparent md:hover:bg-gray-900 md:hover:text-white md:shadow"
                  >
                    {(slides[currentSlide] as any).ctaLabel || t('common.learnMore') || 'Learn More'}
                  </Link>
                )}

                {/* Google Review Badge */}
                <a
                  href="https://www.google.com/maps/place/TN+Cakes+Bali/@-8.6837973,115.1709839,17z/data=!4m8!3m7!1s0x2dd24724ada5a7f7:0x4aca823c3722187c!8m2!3d-8.6837973!4d115.1709839!9m1!1b1!16s%2Fg%2F11gbxbmhls?entry=ttu&g_ep=EgoyMDI1MDczMC4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-white px-3 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-gray-200"
                >
                  {/* Google Logo */}
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                  </div>

                  {/* Rating Content */}
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 text-yellow-400 fill-current" />
                      ))}
                      <span className="text-sm font-bold text-gray-900 ml-1">{config.googleRating.toFixed(1)}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {config.googleReviewCount} {i18n.language === 'id' ? 'Ulasan Google' : 'Google reviews'}
                    </div>
                  </div>
                </a>

                {/* Halal Certification Badge - Only show on halal slide */}
                {config.slides[currentSlide]?.badge?.en === 'Halal Certified' && (
                  <div className="inline-flex items-center gap-3 bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200">
                    <div className="flex-shrink-0">
                      <img src={hallalIcon} alt="Halal Certified" className="w-8 h-8" />
                    </div>
                    <div className="flex flex-col">
                      <div className="text-sm font-bold text-purple-700">Halal Certified</div>
                      <div className="text-xs text-gray-500">ID33110006144950723</div>
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Pills-Style Slide Indicators - Redesigned for mobile */}
      <div className="hidden md:block absolute bottom-8 md:bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-1 md:space-x-2">
          {slides.map((slide, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              onMouseEnter={() => setIsAutoPlay(false)}
              onMouseLeave={() => setIsAutoPlay(true)}
              className={`px-3 py-2 md:px-4 md:py-2 rounded-full text-xs font-medium transition-all duration-300 backdrop-blur-sm ${index === currentSlide
                ? 'bg-white text-red-500 shadow-lg scale-105'
                : 'bg-white/20 text-white hover:bg-white/30 md:bg-white/80 md:text-gray-600 md:hover:text-gray-900 md:hover:bg-gray-100'
                }`}
            >
              <span className="hidden md:inline">{slide.badge}</span>
              <span className="md:hidden w-2 h-2 rounded-full bg-current inline-block"></span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeroSlider;
